import axios from './axiosSetup';

class DpdpConsentConfig {
  constructor() {
    this.listeners = [];
    this.config = this.loadConfig();
  }

  loadConfig() {
    try {
      const stored = localStorage.getItem('dpdp_consent_config');
      if (stored) return JSON.parse(stored);
    } catch (e) {
      console.warn('Failed to load DPDP config', e);
    }

    return {
      tenantId: null,
      apiBase: '',
      appName: 'OneDPDP',
      subBrand: 'Enterprise Privacy',
      title: 'Data Privacy Consent',
      subtitle: 'We respect your privacy. Please review how your personal data will be used.',
      buttonStyle: 'rounded',       // rounded | square | pill
      placement: 'center',          // center (modal) | full (full-screen gate)
      backgroundColor: '#ffffff',
      shade: 'light',               // light | medium | dark
      containerShape: 'rounded',    // rounded | square | rectangle
      size: 'medium',               // small | medium | large | fullscreen
      primaryColor: '#2563eb',
      privacyUrl: '/privacy-policy',
      rightsUrl: '/privacy-policy#rights',
      showStepProgress: true,
      step: 1,
      totalSteps: 2,
      showDataCollected: true,
      requireCheckbox: true,
      complianceLabel: 'is DPDP Act 2023 Compliant',

      // Always-required purposes (non-toggleable)
      purposes: [
        { id: 'service', label: 'Service Delivery', description: 'To provide and improve our core services.', icon: 'doc' },
        { id: 'compliance', label: 'Compliance & Legal', description: 'To comply with applicable laws and regulations.', icon: 'shield' },
        { id: 'comms', label: 'Communications', description: 'To send important updates and notifications.', icon: 'bell' }
      ],

      // Optional, user-toggleable purposes
      categories: [
        { id: 'analytics', label: 'Analytics & Improvement', description: 'To understand usage and improve our services.', defaultOn: true },
        { id: 'marketing', label: 'Marketing & Communication', description: 'To send updates, offers and promotional content.', defaultOn: false }
      ],

      dataCollected: ['Name', 'Email', 'Mobile', 'Documents', 'Usage Data']
    };
  }

  saveConfig(updates) {
    this.config = { ...this.config, ...updates };
    try {
      localStorage.setItem('dpdp_consent_config', JSON.stringify(this.config));
    } catch (e) {
      console.error('Failed to save DPDP config', e);
    }
    this.notifyListeners();
  }

  getConfig() {
    return { ...this.config };
  }

  updateField(field, value) {
    this.saveConfig({ [field]: value });
  }

  updatePurposes(purposes) {
    this.saveConfig({ purposes });
  }

  updateCategories(categories) {
    this.saveConfig({ categories });
  }

  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  notifyListeners() {
    this.listeners.forEach(cb => {
      try { cb(this.config); } catch (e) { console.error('Listener error', e); }
    });
  }

  getTenantIdFromStorage() {
    try { return localStorage.getItem('tenant_id'); } catch (e) { return null; }
  }

  getSessionId() {
    try {
      let sid = localStorage.getItem('dpdp_session_id');
      if (!sid) {
        sid = 's_' + Math.random().toString(36).slice(2, 11);
        localStorage.setItem('dpdp_session_id', sid);
      }
      return sid;
    } catch (e) {
      return 's_' + Math.random().toString(36).slice(2, 11);
    }
  }

  // ─── Reversible symmetric obfuscation / encryption for Tenant ID ───────
  encryptTenantId(tenantId) {
    if (!tenantId && tenantId !== 0) return 'YOUR_TENANT_ID';
    let rawStr = String(tenantId).trim();
    if (!rawStr || rawStr === 'YOUR_TENANT_ID') return 'YOUR_TENANT_ID';
    if (rawStr.startsWith('dpdp_')) {
      const dec = this.decryptTenantId(rawStr);
      if (dec) rawStr = String(dec);
    }
    if (this._cachedRawTenantId === rawStr && this._cachedEncTenantId) {
      return this._cachedEncTenantId;
    }
    const salt = Math.floor(Math.random() * 0xffff).toString(16).padStart(4, '0');
    const secret = 'OneDPDP@2025#PrivacySecretKey';
    const key = secret + salt;
    const payload = `dpdp:${rawStr}:${salt}`;
    let outHex = '';
    for (let i = 0; i < payload.length; i++) {
      const kChar = key.charCodeAt(i % key.length);
      const sChar = salt.charCodeAt(i % salt.length);
      const enc = (payload.charCodeAt(i) ^ kChar ^ sChar) & 0xff;
      outHex += ('0' + enc.toString(16)).slice(-2);
    }
    const encrypted = 'dpdp_' + salt + outHex;
    this._cachedRawTenantId = rawStr;
    this._cachedEncTenantId = encrypted;
    return encrypted;
  }

  decryptTenantId(token) {
    if (!token && token !== 0) return null;
    const str = String(token).trim();
    if (/^\d+$/.test(str)) return parseInt(str, 10);
    if (!str.startsWith('dpdp_') || str.length < 9) {
      const fallback = parseInt(str, 10);
      return isNaN(fallback) ? null : fallback;
    }
    try {
      const body = str.slice(5);
      const salt = body.slice(0, 4);
      const hex = body.slice(4);
      if (hex.length % 2 !== 0) return null;
      const key = 'OneDPDP@2025#PrivacySecretKey' + salt;
      let decrypted = '';
      for (let i = 0; i < hex.length; i += 2) {
        const byteVal = parseInt(hex.substr(i, 2), 16);
        const idx = i / 2;
        const kChar = key.charCodeAt(idx % key.length);
        const sChar = salt.charCodeAt(idx % salt.length);
        decrypted += String.fromCharCode(byteVal ^ kChar ^ sChar);
      }
      const parts = decrypted.split(':');
      if (parts.length === 3 && parts[0] === 'dpdp' && parts[2] === salt) {
        const tid = parseInt(parts[1], 10);
        return isNaN(tid) ? null : tid;
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  // ─── Embed code — points at the standalone dpdp-consent.js widget ───────
  getEmbedCode(bannerId, configOverride) {
    const activeCfg = configOverride || this.config;
    const {
      tenantId, apiBase, appName, subBrand, primaryColor,
      placement, size, privacyUrl, rightsUrl,
      showStepProgress, step, totalSteps, requireCheckbox
    } = activeCfg;

    const rawTid = tenantId || this.getTenantIdFromStorage() || 'YOUR_TENANT_ID';
    const tid = rawTid === 'YOUR_TENANT_ID' ? 'YOUR_TENANT_ID' : this.encryptTenantId(rawTid);
    const apiAttr = apiBase ? ` data-api-base="${apiBase}"` : '';
    const stepAttrs = showStepProgress ? ` data-step="${step}" data-total-steps="${totalSteps}"` : '';
    const bannerAttr = bannerId ? ` data-banner-id="${bannerId}"` : '';

    return (
      `<script src="https://consent.whitehats.tech/widget/dpdp-consent.js"` +
      ` data-tenant="${tid}"` +
      `${bannerAttr}` +
      ` data-app-name="${appName || 'OneDPDP'}"` +
      ` data-sub-brand="${subBrand || 'Enterprise Privacy'}"` +
      ` data-primary-color="${primaryColor || '#2563eb'}"` +
      ` data-mode="${placement === 'full' ? 'page' : 'modal'}"` +
      ` data-size="${size || 'medium'}"` +
      ` data-privacy-url="${privacyUrl || '/privacy-policy'}"` +
      ` data-rights-url="${rightsUrl || '/privacy-policy#rights'}"` +
      ` data-require-checkboxes="${requireCheckbox !== false}"` +
      `${stepAttrs}` +
      `${apiAttr}` +
      `></` + `script>`
    );
  }

  getWidgetStyles(configOverride) {
    const activeCfg = configOverride || this.config;
    const { buttonStyle, backgroundColor, shade, containerShape } = activeCfg;
    const getBorderRadius = () =>
      containerShape === 'rounded' ? '16px' : containerShape === 'rectangle' ? '6px' : '0px';
    const getBoxShadow = () =>
      shade === 'light' ? '0 4px 32px rgba(37,99,235,0.10)'
        : shade === 'medium' ? '0 8px 32px rgba(15,23,42,0.14)'
          : '0 12px 40px rgba(15,23,42,0.24)';
    const getButtonBorderRadius = () =>
      buttonStyle === 'pill' ? '999px' : buttonStyle === 'square' ? '4px' : '12px';
    return {
      container: { backgroundColor: backgroundColor || '#ffffff', borderRadius: getBorderRadius(), boxShadow: getBoxShadow() },
      button: { borderRadius: getButtonBorderRadius() }
    };
  }

  // ─── Backend API Banner Management Helpers ──────────────────────────────
  _getCandidateUrls(path, apiBase) {
    const cleanPath = path.startsWith('/') ? path : '/' + path;
    const base = (apiBase || this.config.apiBase || (typeof window !== 'undefined' && window.__API_BASE__) || '').toString().trim().replace(/\/+$/, '');
    const set = new Set();
    if (base) {
      if (base.endsWith('/api')) {
        set.add(base + cleanPath);
        set.add(base.replace(/\/api$/, '') + cleanPath);
      } else {
        set.add(base + cleanPath);
        set.add(base + '/api' + cleanPath);
      }
    }
    set.add(cleanPath);
    set.add('/api' + cleanPath);
    return Array.from(set);
  }

  async _request(method, path, data = null, params = null, apiBase = '') {
    const urls = this._getCandidateUrls(path, apiBase);
    let lastError = null;
    const token = (typeof localStorage !== 'undefined' && localStorage.getItem('token')) || '';
    const headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    for (const url of urls) {
      try {
        const config = {
          method: method.toLowerCase(),
          url,
          headers,
          data: data ? data : undefined,
          params: params ? params : undefined,
          skipLoading: true
        };
        const response = await axios(config);
        if (response && response.data) {
          return response.data;
        }
      } catch (err) {
        lastError = err;
        const status = err.response ? err.response.status : null;
        // If 404 or 405 (method not allowed / not found), try next candidate URL
        if (status === 404 || status === 405) {
          console.warn(`Endpoint ${url} returned ${status}, trying next candidate...`);
          continue;
        }
        // If it's a 400 validation error (e.g. invalid tenant_id) or 401/403, do not loop
        const msg = err.response?.data?.detail || err.response?.data?.message || err.message || `HTTP ${status}`;
        throw new Error(msg);
      }
    }

    // Secondary fallback using native fetch if axios had network/CORS issue
    for (const url of urls) {
      try {
        let fullUrl = url;
        if (params) {
          const qs = new URLSearchParams(params).toString();
          if (qs) fullUrl += (fullUrl.includes('?') ? '&' : '?') + qs;
        }
        const fetchOpts = {
          method: method.toUpperCase(),
          headers
        };
        if (data && (method.toUpperCase() === 'POST' || method.toUpperCase() === 'PUT')) {
          fetchOpts.body = JSON.stringify(data);
        }
        const fRes = await fetch(fullUrl, fetchOpts);
        if (fRes.status === 404 || fRes.status === 405) {
          continue;
        }
        if (!fRes.ok) {
          let errDetail = 'HTTP ' + fRes.status;
          try {
            const errJson = await fRes.json();
            if (errJson && (errJson.detail || errJson.message)) errDetail = errJson.detail || errJson.message;
          } catch (_) { }
          throw new Error(errDetail);
        }
        return await fRes.json();
      } catch (fErr) {
        if (fErr.message && !fErr.message.includes('404') && !fErr.message.includes('405')) {
          lastError = fErr;
        }
      }
    }

    const finalMsg = lastError?.response?.data?.detail || lastError?.response?.data?.message || lastError?.message || 'Failed to connect to banner server';
    throw new Error(finalMsg);
  }

  async fetchBanners(tenantId, apiBase) {
    const tid = tenantId || this.config.tenantId || this.getTenantIdFromStorage();
    if (!tid) return [];
    try {
      const data = await this._request('GET', '/manager/dpdp-banners', null, { tenant_id: tid }, apiBase);
      if (data && data.limit !== undefined) {
        this.bannerLimit = data.limit;
      }
      return (data && Array.isArray(data.data)) ? data.data : [];
    } catch (e) {
      console.warn('Failed to fetch banners from API:', e);
      return [];
    }
  }

  async fetchBannerData(tenantId, apiBase) {
    const tid = tenantId || this.config.tenantId || this.getTenantIdFromStorage();
    if (!tid) return { data: [], limit: null };
    try {
      const data = await this._request('GET', '/manager/dpdp-banners', null, { tenant_id: tid }, apiBase);
      const limit = data && data.limit !== undefined ? data.limit : null;
      if (limit !== null) this.bannerLimit = limit;
      return {
        data: (data && Array.isArray(data.data)) ? data.data : [],
        limit
      };
    } catch (e) {
      console.warn('Failed to fetch banners from API:', e);
      return { data: [], limit: null };
    }
  }

  async saveBannerToDb(bannerId, payload, tenantId, apiBase) {
    const tid = tenantId || this.config.tenantId || this.getTenantIdFromStorage();
    return await this._request('PUT', `/manager/dpdp-banners/${bannerId}`, payload, { tenant_id: tid }, apiBase);
  }

  async createBannerInDb(payload, apiBase) {
    return await this._request('POST', '/manager/dpdp-banners', payload, null, apiBase);
  }

  async deleteBannerFromDb(bannerId, tenantId, apiBase) {
    const tid = tenantId || this.config.tenantId || this.getTenantIdFromStorage();
    return await this._request('DELETE', `/manager/dpdp-banners/${bannerId}`, null, { tenant_id: tid }, apiBase);
  }

  async setDefaultBannerInDb(bannerId, tenantId, apiBase) {
    const tid = tenantId || this.config.tenantId || this.getTenantIdFromStorage();
    return await this._request('POST', `/manager/dpdp-banners/${bannerId}/set-default`, null, { tenant_id: tid }, apiBase);
  }
}

const dpdpConsentConfig = new DpdpConsentConfig();
if (typeof module !== 'undefined' && module.exports) module.exports = dpdpConsentConfig;
if (typeof window !== 'undefined') window.dpdpConsentConfig = dpdpConsentConfig;
export default dpdpConsentConfig;